import { useEffect, useState } from "react"
import apiClient from "../../Utils/apiClient"
import { useParams } from "react-router-dom"
import { Badge, Button, Card, Col, Row, Tag } from "antd"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { CalendarOutlined } from "@ant-design/icons"
import PanelLayout from "../../Layout/PanelLayout"
import EmptyState from "../../components/EmptyState/EmptyState"
import StatusTag from "../../components/StatusTag/StatusTag"
import { useHeaderContext } from "../../Utils/HeaderContext"
import "../Operations/Operations.scss"

dayjs.extend(relativeTime)

const getExactTimeDiff = (date) => {
  const now = dayjs()
  const input = dayjs(date)
  const diffSeconds = now.diff(input, "second")
  const diffMinutes = now.diff(input, "minute")
  const diffHours = now.diff(input, "hour")
  const diffDays = now.diff(input, "day")

  if (diffSeconds < 60) return `${diffSeconds}s ago`
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

const UserLogs = () => {
  const { render, setRender } = useHeaderContext()

  const { hostname } = useParams()
  const [logs, setLogs] = useState([])
  const [groupedLogs, setGroupedLogs] = useState({})
  const [expandedIndexes, setExpandedIndexes] = useState({})

  useEffect(() => {
    getData()
  }, [hostname, render])

  const getData = async () => {
    try {
      const res = await apiClient(`logs/get-by-host/${hostname}`)
      if (res?.result) {
        const sorted = res.result.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )
        setLogs(sorted)
        groupLogsByDate(sorted)
      }
    } catch (err) {
      console.error("Error fetching logs:", err)
    }
  }
  // const handleOpenLog = async (log) => {
  //   await apiClient("logs/mark-seen", "post", { logId: log._id })
  //   setRender((prev) => !prev)
  // }

  const groupLogsByDate = (allLogs) => {
    const grouped = {}
    allLogs.forEach((log) => {
      const date = dayjs(log.createdAt).format("YYYY-MM-DD")
      if (!grouped[date]) grouped[date] = []
      grouped[date].push(log)
    })

    const sortedGrouped = Object.keys(grouped)
      .sort((a, b) => new Date(b) - new Date(a))
      .reduce((obj, key) => {
        obj[key] = grouped[key]
        return obj
      }, {})

    setGroupedLogs(sortedGrouped)
  }

  const toggleExpand = (logId) => {
    setExpandedIndexes((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }))
  }

  const unread = logs.filter((log) => !log.isOpened).length
  const kpis = [
    {
      key: "total",
      label: "Entries",
      value: logs.length,
      tone: "#2563eb",
      soft: "rgba(59, 130, 246, 0.14)",
    },
    {
      key: "days",
      label: "Days",
      value: Object.keys(groupedLogs).length,
      tone: "#0891b2",
      soft: "rgba(56, 189, 248, 0.14)",
    },
    {
      key: "new",
      label: "Unread",
      value: unread,
      tone: unread ? "#dc2626" : "#059669",
      soft: unread ? "rgba(248, 113, 113, 0.14)" : "rgba(34, 197, 94, 0.14)",
    },
  ]

  return (
    <PanelLayout
      title={`Logs for ${hostname}`}
      subtitle="Activity history for this device."
    >
      <div className="ops-kpis">
        {kpis.map((card) => (
          <div
            key={card.key}
            className="ops-kpi ops-kpi--static"
            style={{ "--act-tone": card.tone, "--act-soft": card.soft }}
          >
            <div>
              <span className="ops-kpi-label">{card.label}</span>
              <strong style={{ fontSize: 17 }}>{card.value}</strong>
            </div>
          </div>
        ))}
      </div>
      <div className="logs">
        {Object.keys(groupedLogs).length > 0 ? (
          Object.keys(groupedLogs).map((date) => (
            <div key={date} style={{ marginBottom: "2rem" }}>
              <p className="mb-3">
                <CalendarOutlined style={{ marginRight: 8 }} />
                {dayjs(date).format("DD MMM YYYY")}
                <Tag color="blue" style={{ marginLeft: 10 }}>
                  {groupedLogs[date].length} Logs
                </Tag>
              </p>

              {groupedLogs[date].map((log) => {
                const isExpanded = expandedIndexes[log._id]

                return (
                  // <Badge.Ribbon
                  //   key={log._id}
                  //   text={!log.isOpened ? "New" : log.status}
                  //   color={
                  //     !log.isOpened
                  //       ? "red"
                  //       : log.status === "pending"
                  //       ? "orange"
                  //       : log.status === "working"
                  //       ? "blue"
                  //       : log.status === "success"
                  //       ? "green"
                  //       : "cyan"
                  //   }
                  // >
                  <Card style={{ marginBottom: "1rem" }}>
                    <Row gutter={[12, 2]}>
                      <Col sm={24} md={24} lg={12}>
                        <h3 className="fw-bold font-18 text-capitalize">
                          {`${log.createdFor} Log`}
                        </h3>
                        <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                          <li>
                            <strong>Created At: </strong>
                            {dayjs(log.createdAt).format("hh:mm A")}
                            <span style={{ color: "#888", marginLeft: 8 }}>
                              {getExactTimeDiff(log.createdAt)}
                            </span>
                          </li>
                        </ul>

                        <Button
                          onClick={() => toggleExpand(log._id)}
                          type="default"
                          size="small"
                          className="mt-2"
                        >
                          {isExpanded ? "Show Less" : "Show More ..."}
                        </Button>
                      </Col>

                      <Col sm={24} md={24} lg={12}>
                        <ul className="mb-0 ps-3 text-capitalize">
                          <li>
                            <strong>User :</strong> {log.createdFor}
                          </li>
                          <li>
                            <strong>Command :</strong> {log.title}
                          </li>
                          <li>
                            <strong>Status :</strong>{" "}
                            <StatusTag status={log.status} />
                          </li>
                        </ul>
                      </Col>
                    </Row>

                    {isExpanded && (
                      <>
                        {log.data &&
                          log.data.map((item, i) => (
                            <div key={i} className="mb-2">
                              <Card>
                                <ul className="mb-0 text-capitalize">
                                  {Object.entries(item).map(
                                    ([key, value], idx) => (
                                      <li key={idx}>
                                        <strong>
                                          {key.replace(/([A-Z])/g, " $1")}:{" "}
                                        </strong>
                                        {String(value)}
                                      </li>
                                    )
                                  )}
                                </ul>
                              </Card>
                            </div>
                          ))}

                        {log.output && (
                          <p>
                            <strong>Output :</strong>{" "}
                            {log.output !== "pending" ? log.output : log.status}
                          </p>
                        )}
                      </>
                    )}
                  </Card>
                  // </Badge.Ribbon>
                )
              })}
            </div>
          ))
        ) : (
          <EmptyState
            title="No logs for this host"
            description="Activity will appear here as tasks complete on this device."
          />
        )}
      </div>
    </PanelLayout>
  )
}

export default UserLogs
